/**
 * Brand Voice Framework for Castmagic
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Brand Voice Framework for Castmagic | Your Smart Stack</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
    --sage: oklch(0.38 0.09 155);
    --sage-light: oklch(0.38 0.09 155 / 0.10);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 80px;
  }

  .container {
    max-width: 700px;
    margin: 0 auto;
  }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 12px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(26px, 5vw, 36px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header .subtitle {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 520px;
    margin: 0 auto;
  }

  /* STEP SECTIONS */
  .step {
    margin-bottom: 40px;
  }

  .step-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Fraunces', Georgia, serif;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
  }

  /* FIELD CARDS */
  .field-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-left: 4px solid var(--terracotta);
    border-radius: 8px;
    padding: 20px 22px;
    margin-bottom: 14px;
  }

  .field-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 6px;
  }

  .field-hint {
    font-size: 14px;
    color: var(--muted);
    margin-bottom: 10px;
    font-style: italic;
  }

  .field-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    color: var(--espresso);
    background: var(--cream);
    transition: border-color 0.2s;
    resize: vertical;
    min-height: 44px;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--terracotta);
    background: var(--white);
  }

  .field-input::placeholder {
    color: oklch(0.88 0.012 85);
  }

  /* PROMPT BUILDER */
  .prompt-builder {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 28px;
    margin-bottom: 40px;
  }

  .prompt-builder-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
  }

  .prompt-builder h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--espresso);
  }

  .prompt-output {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.7;
    color: var(--body);
    min-height: 120px;
    white-space: pre-wrap;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  }

  .prompt-output .placeholder-text {
    color: oklch(0.88 0.012 85);
    font-style: italic;
  }

  .copy-btn {
    margin-top: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    background: var(--terracotta);
    color: var(--white);
    border: none;
    border-radius: 6px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }

  .copy-btn:hover { background: oklch(0.26 0.075 255); }
  .copy-btn:active { transform: scale(0.98); }
  .copy-btn.copied { background: var(--sage); }

  /* EXAMPLE SECTION */
  .example-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 24px;
    margin-bottom: 40px;
  }

  .example-card h3 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--espresso);
    margin-bottom: 16px;
  }

  .example-row {
    margin-bottom: 18px;
  }

  .example-row-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 8px;
  }

  .example-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 14px 16px;
    font-size: 14px;
    line-height: 1.6;
    color: var(--body);
  }

  .example-box.output {
    border-left: 4px solid var(--sage);
    background: var(--sage-light);
    color: var(--sage);
    font-style: italic;
  }

  /* SAVE TIP */
  .save-tip {
    background: var(--terracotta-light);
    border: 1px solid var(--terracotta-mid);
    border-radius: 10px;
    padding: 22px 24px;
    margin-bottom: 40px;
  }

  .save-tip-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .save-tip-icon {
    width: 28px;
    height: 28px;
    color: var(--terracotta);
    flex-shrink: 0;
  }

  .save-tip h3 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--espresso);
  }

  .save-tip ol {
    padding-left: 20px;
    color: var(--body);
    font-size: 14px;
    line-height: 1.8;
  }

  .save-tip ol li {
    margin-bottom: 4px;
  }

  /* DIVIDER */
  .section-divider {
    border: none;
    border-top: 1px solid var(--border);
    margin: 36px 0;
  }

  /* FOOTER */
  .footer {
    text-align: center;
    padding-top: 32px;
    border-top: 1px solid var(--border);
  }

  .footer-quote {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-style: italic;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 10px;
  }

  .footer-attr {
    font-size: 12px;
    color: var(--terracotta);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header">
    <div class="eyebrow">Castmagic Resource</div>
    <h1>Brand Voice Framework</h1>
    <p class="subtitle">A fill-in-the-blank framework so every Castmagic output sounds exactly like you, not like AI.</p>
  </div>

  <!-- STEP 1 -->
  <div class="step">
    <div class="step-header">
      <div class="step-number">1</div>
      <div class="step-title">Define Your Voice Attributes</div>
    </div>

    <div class="field-card">
      <div class="field-label">Tone of Voice</div>
      <div class="field-hint">e.g. "Friendly and conversational, but also authoritative."</div>
      <textarea class="field-input" id="tone" rows="2" placeholder="Casual / professional / inspirational / educational / bold / empathetic…"></textarea>
    </div>

    <div class="field-card">
      <div class="field-label">Your Audience</div>
      <div class="field-hint">e.g. "Entrepreneurs who feel overwhelmed by technology."</div>
      <textarea class="field-input" id="audience" rows="2" placeholder="Small business owners / coaches / tech beginners / entrepreneurs…"></textarea>
    </div>

    <div class="field-card">
      <div class="field-label">Style Preference</div>
      <div class="field-hint">e.g. "Short, energetic sentences with storytelling elements."</div>
      <textarea class="field-input" id="style" rows="2" placeholder="Short sentences / storytelling / step-by-step / motivational / academic…"></textarea>
    </div>

    <div class="field-card">
      <div class="field-label">Keywords or Phrases to Include</div>
      <div class="field-hint">e.g. "Work smarter, not harder" or "Tech is the great equalizer."</div>
      <textarea class="field-input" id="include" rows="2" placeholder="Your signature phrases, slogans, hashtags…"></textarea>
    </div>

    <div class="field-card">
      <div class="field-label">What to Avoid</div>
      <div class="field-hint">e.g. "Avoid corporate buzzwords and heavy tech jargon."</div>
      <textarea class="field-input" id="avoid" rows="2" placeholder="Jargon / overly formal / salesy language / technical terms…"></textarea>
    </div>
  </div>

  <hr class="section-divider">

  <!-- STEP 2: LIVE PROMPT BUILDER -->
  <div class="step">
    <div class="step-header">
      <div class="step-number">2</div>
      <div class="step-title">Your Castmagic Prompt</div>
    </div>
  </div>

  <div class="prompt-builder">
    <div class="prompt-builder-header">
      <svg class="save-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M9 3v18M15 9h3M15 13h3"/>
      </svg>
      <h2>Live Prompt Builder</h2>
    </div>
    <p style="font-size: 14px;color:var(--muted);margin-bottom:14px;">Fill in the fields above. Your prompt updates automatically below.</p>
    <div class="prompt-output" id="promptOutput">
      <span class="placeholder-text">Your personalised Castmagic prompt will appear here as you fill in your voice attributes above…</span>
    </div>
    <button class="copy-btn" id="copyBtn" onclick="copyPrompt()">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg>
      Copy Prompt
    </button>
  </div>

  <hr class="section-divider">

  <!-- STEP 3: EXAMPLE -->
  <div class="step">
    <div class="step-header">
      <div class="step-number">3</div>
      <div class="step-title">Example in Action</div>
    </div>
  </div>

  <div class="example-card">
    <h3>See how it works</h3>

    <div class="example-row">
      <div class="example-row-label">Your Voice Attributes</div>
      <div class="example-box">
        <strong>Tone:</strong> Friendly and motivational<br>
        <strong>Audience:</strong> Small business owners<br>
        <strong>Style:</strong> Storytelling + step-by-step tips<br>
        <strong>Include:</strong> "Work smarter, not harder"<br>
        <strong>Avoid:</strong> Corporate jargon
      </div>
    </div>

    <div class="example-row">
      <div class="example-row-label">The Prompt You'd Use in Castmagic</div>
      <div class="example-box">
        "Rewrite this transcript in my brand voice. Tone: friendly and motivational. Audience: small business owners. Style: storytelling + step-by-step tips. Include the phrase 'work smarter, not harder.' Avoid corporate jargon."
      </div>
    </div>

    <div class="example-row" style="margin-bottom:0">
      <div class="example-row-label">Castmagic Output</div>
      <div class="example-box output">
        "Running a business is tough, I get it. But here's the truth: you don't need to do it all manually anymore. With today's tools, you can work smarter, not harder. Here are 3 simple ways I saved 10 hours this week…"
      </div>
    </div>
  </div>

  <hr class="section-divider">

  <!-- STEP 4: SAVE & REUSE -->
  <div class="step">
    <div class="step-header">
      <div class="step-number">4</div>
      <div class="step-title">Save &amp; Reuse</div>
    </div>
  </div>

  <div class="save-tip">
    <div class="save-tip-header">
      <svg class="save-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
        <polyline points="17 21 17 13 7 13 7 21"/>
        <polyline points="7 3 7 8 15 8"/>
      </svg>
      <h3>Make it permanent in Castmagic</h3>
    </div>
    <ol>
      <li>Copy your prompt using the button above.</li>
      <li>In Castmagic, go to <strong>Custom Prompts</strong> and create a new prompt.</li>
      <li>Label it <strong>"My Brand Voice"</strong>.</li>
      <li>Paste your prompt and save.</li>
      <li>Use it for every transcript or output you generate: your content will always sound like you.</li>
    </ol>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    <p class="footer-quote">"The goal isn't more content. It's content that sounds unmistakably like you."</p>
    <p class="footer-attr">Your Smart Stack Quick Wins · Castmagic</p>
  </div>

</div>

<script>
  const fields = ['tone', 'audience', 'style', 'include', 'avoid'];

  function buildPrompt() {
    const tone = document.getElementById('tone').value.trim();
    const audience = document.getElementById('audience').value.trim();
    const style = document.getElementById('style').value.trim();
    const include = document.getElementById('include').value.trim();
    const avoid = document.getElementById('avoid').value.trim();

    const hasAny = tone || audience || style || include || avoid;
    const output = document.getElementById('promptOutput');

    if (!hasAny) {
      output.innerHTML = '<span class="placeholder-text">Your personalised Castmagic prompt will appear here as you fill in your voice attributes above…</span>';
      return;
    }

    let prompt = 'Rewrite this transcript in my brand voice.';
    if (tone) prompt += '\\n\\nTone: ' + tone + '.';
    if (audience) prompt += '\\nAudience: ' + audience + '.';
    if (style) prompt += '\\nStyle: ' + style + '.';
    if (include) prompt += '\\nInclude: ' + include + '.';
    if (avoid) prompt += '\\nAvoid: ' + avoid + '.';

    output.textContent = prompt;
  }

  fields.forEach(id => {
    document.getElementById(id).addEventListener('input', buildPrompt);
  });

  function copyPrompt() {
    const output = document.getElementById('promptOutput');
    const text = output.textContent;
    if (!text || text.includes('Your personalised')) return;

    navigator.clipboard.writeText(text).then(() => {
      const btn = document.getElementById('copyBtn');
      btn.classList.add('copied');
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
      setTimeout(() => {
        btn.classList.remove('copied');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy Prompt';
      }, 2000);
    });
  }
</script>
</body>
</html>
`;

export default function CastmagicBrandVoice() {
  return (
    <GuideShell
      context={"Castmagic guide"}
      backHref={"/resources/castmagic"}
      backLabel={"Back to Castmagic"}
    >
      <GuideFrame html={HTML_SRC} title={"Brand Voice Framework for Castmagic"} />
    </GuideShell>
  );
}
